import React, { MouseEvent, ReactElement, useMemo } from 'react';

import { rem } from '@vimeo/bokeh';
import { Archive, PinOffFilled, PinOn, Unarchive, CircleCheck } from '@vimeo/bokeh-icons';
import { Callable, Id } from '@vimeo/core/types/generic';
import { BokehIconButton, BokehTooltip } from '@vimeo/live-core/components/bokeh';

import { createInteractionDomName } from '../../../../libs/shared/utils/dom/identifying';
import { translations } from '../../../../translations';

interface IActionButtonDescriber {
  id: string;
  icon: ReactElement;
  tooltipLabel: string;
  onClick: Callable | ((event: MouseEvent) => void);
}

interface IQuestionItemActionsProps {
  className: Id;
  isPending: boolean;
  isArchived: boolean;
  isPinned: boolean;
  hasActiveMenu?: boolean;
  onQuestionArchiveClicked: Callable;
  onQuestionUnArchiveClicked: Callable;
  onQuestionApproveClicked: Callable;
  onQuestionUnPinClicked: (event: MouseEvent) => void;
  onQuestionPinClicked: (event: MouseEvent) => void;
}

export function QuestionItemActions({
  className,
  isPending,
  isArchived,
  isPinned,
  hasActiveMenu = false,
  onQuestionArchiveClicked,
  onQuestionUnArchiveClicked,
  onQuestionApproveClicked,
  onQuestionUnPinClicked,
  onQuestionPinClicked,
}: IQuestionItemActionsProps) {
  const buttons: Array<IActionButtonDescriber> = useMemo(() => {
    if (isPending) {
      return [
        {
          id: 'archive',
          icon: <Archive />,
          tooltipLabel: translations.archive,
          onClick: onQuestionArchiveClicked,
        },
        {
          id: 'approve',
          icon: <CircleCheck />,
          tooltipLabel: translations.approve,
          onClick: onQuestionApproveClicked,
        },
      ];
    }

    if (isArchived) {
      return [
        {
          id: 'unarchive',
          icon: <Unarchive />,
          tooltipLabel: translations.unarchive,
          onClick: onQuestionUnArchiveClicked,
        },
      ];
    }

    const activeButtons: Array<IActionButtonDescriber> = [
      {
        id: isPinned ? 'unpin' : 'pin',
        icon: isPinned ? <PinOffFilled /> : <PinOn />,
        tooltipLabel: isPinned ? translations.unpin : translations.pinToTop,
        onClick: isPinned ? onQuestionUnPinClicked : onQuestionPinClicked,
      },
    ];

    if (!hasActiveMenu) {
      activeButtons.push({
        id: 'archive',
        icon: <Archive />,
        tooltipLabel: translations.archive,
        onClick: onQuestionArchiveClicked,
      });
    }

    return activeButtons;
  }, [
    isArchived,
    isPending,
    isPinned,
    hasActiveMenu,
    onQuestionApproveClicked,
    onQuestionArchiveClicked,
    onQuestionPinClicked,
    onQuestionUnArchiveClicked,
    onQuestionUnPinClicked,
  ]);

  return (
    <>
      {buttons.map(({ id, icon, tooltipLabel, onClick }) => (
        <BokehTooltip
          key={id}
          placement={'top'}
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                fallbackPlacements: ['top-end'],
              },
            },
          ]}
          label={tooltipLabel}
        >
          <BokehIconButton
            className={createInteractionDomName(className, id)}
            size={'xs'}
            icon={icon}
            padding={'0!important'}
            width={rem(24)}
            variant={'secondary'}
            onClick={onClick}
          />
        </BokehTooltip>
      ))}
    </>
  );
}
